import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import PlusIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { removeInquiry } from '../store/slices/inquiriesSlice';
import { addOrUpdateNotesOnInquiries } from '../store/slices/notesSlice';
import { setInitialCallFormPrimingInfo } from '../store/slices/intendedInfoSlice';

import {
  createStdRequest,
  apiFetch,
  dateStringForDateTime,
  calculatePhoneNumberChange,
  validateEmail,
  validatePhoneNumber,
  showAlert,
  isFirmReferrerType,
  isProprietorshipType,
  isPractitionerType,
  sortedDropdownItemLabels,
} from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import SocketAccess from '../comm/SocketAccess';

import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import KSelect from '../components/KSelect';
import InquiryConversionModal from '../components/InquiryConversionModal';
import CrmTextField from '../components/CrmTextField';
import CrmButton from '../components/CrmButton';
import DocsAssocGroup from '../components/DocsAssocGroup';
import { searchableContactDataBuilder } from '../components/SearchableContactHierarchy';
import ReferralSelectionHierarchy from '../components/ReferralSelectionHierarchy';
import IcfConsultancyModal from '../components/IcfConsultancyModal';
import IcfReferrerFirmModal from '../components/IcfReferrerFirmModal';
import IcfPractitionerModal from '../components/IcfPractitionerModal';
import IcfOrgContactModal from '../components/IcfOrgContactModal';
import IcfUnaffiliatedContactModal from '../components/IcfUnaffiliatedContactModal';

const styles = {
  root: {},
  pageTitle: {
    marginLeft: '20px',
    marginTop: '16px',
    fontFamily: 'MetricWebBold, sans-serif',
    fontWeight: 500,
    fontSize: '1.7rem',
  },
  selectionHierarchy: {
    marginLeft: '16px',
    marginRight: '4px',
  },
  valueDiv: {
    marginTop: '20px',
    marginLeft: '16px',
  },
  buttonsDiv: {
    marginTop: '20px',
    textAlign: 'right',
  },
  nativeInput: {},
  tooltip: {
    fontSize: '90%',
  },
  errorMessageDiv: {
    marginTop: '8px',
    color: 'red',
    fontWeight: 500,
  },
  newNoteButton: {
    marginTop: '10px',
    marginLeft: '15px',
    color: '#ff8c6e',
    fontWeight: 'bold',
  },
  divMessage: {
    width: '95% !important',
  },
  delIcon: {
    marginTop: '10px',
    marginLeft: '15px',
    cursor: 'pointer',
  },
};

function TooltippedButton(props) {
  if (props.disabled) {
    return (
      <CrmButton disabled outlined={!props.filled} filled={props.filled}>
        {props.text}
      </CrmButton>
    );
  } else {
    return (
      <Tooltip title={props.tip} placement="top" classes={{ tooltip: props.classes.tooltip }}>
        <div style={{ display: 'inline-block' }}>
          <CrmButton outlined={!props.filled} filled={props.filled} onClick={props.action}>
            {props.text}
          </CrmButton>
        </div>
      </Tooltip>
    );
  }
}

let temperatureOptions = null;

class InquiryPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      redirect: undefined,
      shouldShowConversionModal: false,
      date: DateTime.local(),
      name: '',
      email: '',
      phone: '',
      referralSourceCategory: 'Company / Organization',
      referralOrgType: '',
      referralOrgId: 0,
      referralContactId: 0,
      chosenWordOfMouthOption: '',
      chosenSocialMediaOption: '',
      chosenMediaOption: '',
      referralOptionalSpecies: '',
      source: '',
      campaignId: 0,
      ownerPersonId: 0,
      message: '',
      temperature: 'Unknown',
      extras: [],
      notes: [],
      deletedNoteIds: [],
      docs: [],
      deletedDocIds: [],
      isDocModified: false,

      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
      shouldShowNewOrgContactModalStateVar: false,
      shouldShowNewUnaffilContactModalStateVar: false,
    };
  }

  inquiryId = 0;
  userPersonId = 0;
  callRailCallId = 0;

  originalName = '';
  originalEmail = '';
  originalPhone = '';
  originalReferralSourceCategory = 'Company / Organization';
  originalReferralOrgType = '';
  originalReferralOrgId = 0;
  originalReferralContactId = 0;
  originalWordOfMouthType = '';
  originalSocialMediaType = '';
  originalMediaType = '';
  originalOptionalSpecies = '';
  originalCampaignId = 0;
  originalOwnerPersonId = 0;
  originalMessage = '';
  originalTemperature = '';
  notesHaveChanged = false;

  searchableContactData = searchableContactDataBuilder.build();

  componentDidMount() {
    if (!temperatureOptions) {
      temperatureOptions = sortedDropdownItemLabels('Lead Temperature');
    }
    const inquiry = DA.getInquiryById(this.props.inquiryId);
    this.inquiryId = inquiry.inquiryId;
    this.callRailCallId = inquiry.callRailCallId;
    this.setStateFromInquiry(inquiry);
    this.userPersonId = DA.getLoggedInUserInfo().personId;
    this.registerRefreshCallbacks();
  }

  setStateFromInquiry(inquiry) {
    const name = (inquiry.firstName + ' ' + inquiry.lastName).trim();
    const notes = DA.getNotesAboutInquiryWithId(inquiry.inquiryId);
    console.log(`in setStateFromInquiry(), nNotes: ${notes.length}`);
    const sortedNotes = notes.sort((a, b) => (a.date.valueOf() > b.date.valueOf() ? 1 : -1));

    const docs = DA.getDocumentsAboutInquiryWithId(inquiry.inquiryId);
    const sortedDocs = docs.sort((a, b) => (a.publicName.toLowerCase() > b.publicName.toLowerCase() ? 1 : -1));
    const possiblyAmendedReferralSourceCategory = inquiry.referralSourceCategory || 'Company / Organization';

    const newState = {
      ...inquiry,
      chosenWordOfMouthOption: inquiry.referralWordOfMouthType,
      chosenSocialMediaOption: inquiry.referralSocialMediaType,
      chosenMediaOption: inquiry.referralMediaType,
      referralOptionalSpecies: inquiry.referralSourceSpecies,
      name,
      notes: sortedNotes,
      docs: sortedDocs,
      deletedNoteIds: [],
      deletedDocIds: [],
      isDocModified: false,
      sillyRefreshToggle: false,
      referralSourceCategory: possiblyAmendedReferralSourceCategory,
    };
    this.originalName = name;
    this.originalEmail = inquiry.email;
    this.originalPhone = inquiry.phone;
    this.originalReferralSourceCategory = possiblyAmendedReferralSourceCategory;
    this.originalReferralOrgType = inquiry.referralOrgType;
    this.originalReferralOrgId = inquiry.referralOrgId;
    this.originalReferralContactId = inquiry.referralContactId;
    this.originalWordOfMouthType = inquiry.referralWordOfMouthType;
    this.originalSocialMediaType = inquiry.referralSocialMediaType;
    this.originalMediaType = inquiry.referralMediaType;
    this.originalOptionalSpecies = inquiry.referralSourceSpecies;
    this.originalCampaignId = inquiry.campaignId;
    this.originalOwnerPersonId = inquiry.ownerPersonId;
    this.originalMessage = inquiry.message;
    this.originalTemperature = inquiry.temperature;
    this.notesHaveChanged = false;
    this.setState(newState);
  }

  registerRefreshCallbacks() {
    const inquiryCallbackStruct = {
      kind: 'inquiry',
      pageName: 'InquiryPage',
      refreshFunc: () => this.redisplayPageForChangedInquiry(),
    };
    SocketAccess.registerRefreshCallback(inquiryCallbackStruct);

    const noteCallbackStruct = {
      kind: 'note:onInquiry',
      pageName: 'InquiryPage',
      refreshFunc: () => this.redisplayPageForChangedNotes(),
    };
    SocketAccess.registerRefreshCallback(noteCallbackStruct);

    const documentCallbackStruct = {
      kind: 'document:onInquiry',
      pageName: 'InquiryPage',
      refreshFunc: () => this.redisplayPageForChangedDocuments(),
    };
    SocketAccess.registerRefreshCallback(documentCallbackStruct);
  }

  redisplayPageForChangedInquiry() {
    this.setState({ sillyRefreshToggle: !this.state.sillyRefreshToggle });
  }

  redisplayPageForChangedNotes() {
    const notes = DA.getNotesAboutInquiryWithId(this.state.inquiryId);
    const augmentedNotes = notes
      .sort((a, b) => (a.date.valueOf() > b.date.valueOf() ? 1 : -1))
      .map((note) => {
        return { ...note, oldContent: note.content };
      });

    this.setState({ notes: augmentedNotes });
  }

  redisplayPageForChangedDocuments() {
    const docs = DA.getDocumentsAboutInquiryWithId(this.state.inquiryId);
    const sortedDocs = docs.sort((a, b) => (a.publicName.toLowerCase() > b.publicName.toLowerCase() ? 1 : -1));
    this.setState({ docs: sortedDocs });
  }

  componentWillUnmount() {
    SocketAccess.unregisterRefreshCallbacksForPage('InquiryPage');
  }

  stateIsOriginal() {
    return (
      this.originalName === this.state.name &&
      this.originalEmail === this.state.email &&
      this.originalPhone === this.state.phone &&
      this.originalReferralSourceCategory === this.state.referralSourceCategory &&
      this.originalReferralOrgType === this.state.referralOrgType &&
      this.originalReferralOrgId === this.state.referralOrgId &&
      this.originalReferralContactId === this.state.referralContactId &&
      this.originalWordOfMouthType === this.state.chosenWordOfMouthOption &&
      this.originalSocialMediaType === this.state.chosenSocialMediaOption &&
      this.originalMediaType === this.state.chosenMediaOption &&
      this.originalOptionalSpecies === this.state.referralOptionalSpecies &&
      this.originalCampaignId === this.state.campaignId &&
      this.originalOwnerPersonId === this.state.ownerPersonId &&
      this.originalMessage === this.state.message &&
      this.originalTemperature === this.state.temperature &&
      !this.notesHaveChanged &&
      !this.state.isDocModified
    );
  }

  handleNumberChange(event) {
    const oldValue = this.state.phone;
    const newValue = event.target.value.trim();
    const phChange = calculatePhoneNumberChange(oldValue, newValue);
    if (phChange.shouldUpdate) {
      this.setState({ phone: phChange.newValue });
    }
  }

  handleReferralOrgTypeChange(newValue) {
    this.setState(
      {
        referralOrgType: newValue,
        referralOrgId: 0,
        referralContactId: 0,
        relationship: '',
        acquaintanceship: '',
        referrerPersonId: 0,
      },
      () => this.preselectSingleItemLists()
    );
  }

  preselectSingleItemLists() {
    if (isFirmReferrerType(this.state.referralOrgType)) {
      const knownFirmsOfReferrerType = this.getKnownFirmsOfReferrerType();
      if (knownFirmsOfReferrerType.length === 1) {
        const uniqueFirm = knownFirmsOfReferrerType[0];
        const uniqueId = uniqueFirm.id;
        if (!this.state.referralOrgId) {
          this.setState({ referralOrgId: uniqueId });
          const contactIds = DA.getFirmById(uniqueId).contactIds;
          if (contactIds.length === 1) {
            this.setState({ referralContactId: contactIds[0] });
          }
        }
      }
    }
  }

  getKnownFirmsOfReferrerType() {
    return this.props.knownFirms.filter((firm) => firm.firmType === this.state.referralOrgType);
  }

  getKnownPractices() {
    return this.props.knownFirms.filter((firm) => firm.firmType === 'Private Practitioner');
  }

  forceShowingOfNewReferrerCompanyModal(referralOrgType) {
    if (isProprietorshipType(referralOrgType)) {
      this.setState({ referralOrgType, shouldShowProprietorshipModalStateVar: true });
    } else if (isPractitionerType(referralOrgType)) {
      this.setState({ referralOrgType, shouldShowPractitionerModalStateVar: true });
    } else {
      this.setState({ referralOrgType, shouldShowReferrerFirmModalStateVar: true });
    }
  }

  forceShowingOfNewContactModal(referralOrgType, firmId) {
    if (firmId) {
      this.forceShowingOfNewOrgContactModal(referralOrgType, firmId);
    } else {
      this.forceShowingOfNewUnaffiliatedContactModal(referralOrgType, firmId);
    }
  }

  forceShowingOfNewOrgContactModal(referralOrgType, firmId) {
    this.setState({ referralOrgType, referralOrgId: firmId, shouldShowNewOrgContactModalStateVar: true });
  }

  forceShowingOfNewUnaffiliatedContactModal(referralOrgType, firmId) {
    this.setState({ referralOrgType, referralOrgId: firmId, shouldShowNewUnaffilContactModalStateVar: true });
  }

  shouldShowReferrerFirmModal() {
    return this.state.shouldShowReferrerFirmModalStateVar;
  }

  shouldShowProprietorshipModal() {
    return this.state.shouldShowProprietorshipModalStateVar;
  }

  shouldShowPractitionerModal() {
    return this.state.shouldShowPractitionerModalStateVar;
  }

  shouldShowNewOrgContactModal() {
    return this.state.shouldShowNewOrgContactModalStateVar;
  }

  shouldShowNewUnaffilContactModal() {
    return this.state.shouldShowNewUnaffilContactModalStateVar;
  }

  handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referralOrgType: visibleFirmType,
      referralOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewReferrerFirmSuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referralOrgType: visibleFirmType,
      referralOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({
      referralOrgType: visibleFirmType,
      referralOrgId: visibleFirmId,
      referralContactId: contactId,
      shouldShowReferrerFirmModalStateVar: false,
      shouldShowProprietorshipModalStateVar: false,
      shouldShowPractitionerModalStateVar: false,
    });
  }

  handleNewOrgContactSuccess(contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({ referralContactId: contactId, shouldShowNewOrgContactModalStateVar: false });
  }

  handleNewUnaffilContactSuccess(contactId) {
    this.searchableContactData = searchableContactDataBuilder.build();
    this.setState({ referralContactId: contactId, shouldShowNewUnaffilContactModalStateVar: false });
  }

  cancelReferrerFirmModal() {
    if (isFirmReferrerType(this.state.referralOrgType)) {
      const knownFirmsOfReferrerType = this.getKnownFirmsOfReferrerType();
      if (knownFirmsOfReferrerType.length === 0) {
        this.setState({ referralOrgType: '' });
      }
      this.setState({ referralOrgId: 0, shouldShowReferrerFirmModalStateVar: false });
    }
  }

  cancelPractitionerModal() {
    const knownPractices = this.getKnownPractices();
    if (knownPractices.length === 0) {
      this.setState({ referralOrgType: '' });
    }
    this.setState({ referralOrgId: 0, shouldShowPractitionerModalStateVar: false });
  }

  cancelContactModal() {
    this.setState({
      shouldShowNewOrgContactModalStateVar: false,
      shouldShowNewUnaffilContactModalStateVar: false,
      shouldAddEmergencyContact: false,
    });
  }

  discardChanges() {
    this.setState({
      name: this.originalName,
      email: this.originalEmail,
      phone: this.originalPhone,
      referralSourceCategory: this.originalReferralSourceCategory,
      referralOrgType: this.originalReferralOrgType,
      referralOrgId: this.originalReferralOrgId,
      referralContactId: this.originalReferralContactId,
      chosenWordOfMouthOption: this.originalWordOfMouthType,
      chosenSocialMediaOption: this.originalSocialMediaType,
      chosenMediaOption: this.originalMediaType,
      referralOptionalSpecies: this.originalOptionalSpecies,
      campaignId: this.originalCampaignId,
      ownerPersonId: this.originalOwnerPersonId,
    });
  }

  saveChanges() {
    const validation = this.validateStateForUpdate();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }
    const inquiryId = this.state.inquiryId;
    const uri = `/api/inquiry/${inquiryId}`;
    const apiStruct = this.buildApiStructForUpdate();
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.saveInquiryNotes();
      this.saveInquiryDocuments();
      ReduxLoader.updateInquiry(apiStruct);
      const inquiryWithRealDate = DA.resolveInquiryDates(apiStruct);
      this.setStateFromInquiry(inquiryWithRealDate);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveInquiryNotes() {
    // Added for debugging:
    const nNotes = this.state.notes.length;
    console.log(`At start of saveInquiryNotes(), this.state.notes.length: ${nNotes}`);

    this.state.notes.forEach((note) => {
      if (note.noteId === 0) {
        console.log(`calling saveAddedNoteToDbAndRedux()`);
        this.saveAddedNoteToDbAndRedux(note);
      } else if (note.oldContent !== note.content) {
        this.saveUpdatedNoteToDbAndRedux(note);
      }
    });

    this.state.deletedNoteIds.forEach((noteId) => {
      if (noteId > 0) {
        this.deleteNoteFromDb(noteId);
        ReduxLoader.removeNoteOnInquiry(noteId);
      }
    });
  }

  saveAddedNoteToDbAndRedux(note) {
    const uri = `/api/note`;
    const apiStruct = this.buildApiStructForNote(note);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.addOrUpdateNotesOnInquiries([json]);
      this.redisplayPageForChangedNotes();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveUpdatedNoteToDbAndRedux(note) {
    const uri = `/api/note/${note.noteId}`;
    const apiStruct = this.buildApiStructForNote(note);
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.addOrUpdateNotesOnInquiries([note]);
      this.redisplayPageForChangedNotes();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deleteNoteFromDb(noteId) {
    const uri = `/api/note/${noteId}`;
    const request = createStdRequest(uri, 'DELETE');
    apiFetch(this, request, () => {});
  }

  buildApiStructForNote(note) {
    const inquiryId = this.state.inquiryId;
    const authorId = this.userPersonId;
    const viewingFirmId = DA.getFirmId();
    const date = note.date.toISO();
    return {
      ...note,
      date,
      inquiryId,
      authorId,
      viewingFirmId,
    };
  }

  saveInquiryDocuments() {
    this.state.docs.forEach((doc) => {
      if (doc.storedDocumentId === 0) {
        this.saveAddedDocToDb(doc);
      } else if (doc.oldTypeTag !== doc.typeTag) {
        this.saveUpdatedDocToDb(doc);
      }
    });

    this.state.deletedDocIds.forEach((docId) => {
      if (docId > 0) {
        this.deleteDocFromDb(docId);
      }
    });
  }

  saveAddedDocToDb(doc) {
    const uri = `/api/document/viewingFirm/${DA.getFirmId()}/inquiry/${doc.inquiryId}`;
    const formData = new FormData();
    formData.append('file', doc.data);
    fetch(uri, { method: 'POST', body: formData })
      .then((response) => response.json())
      .then(async (result) => {
        if (await result) {
          const { typeTag, description } = doc;
          if (description.trim() !== '' || (typeTag !== '' && typeTag !== 'None')) {
            this.updateDocumentToDb(typeTag, description, result.storedDocumentId);
          }
        }
      })
      .catch((error) => console.log(error));
  }

  saveUpdatedDocToDb(doc) {
    const { typeTag, description, storedDocumentId } = doc;
    this.updateDocumentToDb(typeTag, description, storedDocumentId);
  }

  deleteDocFromDb(docId) {
    const uri = `/api/document/${docId}`;
    const request = createStdRequest(uri, 'DELETE');
    apiFetch(this, request, () => {});
  }

  updateDocumentToDb(documentType, description, documentId) {
    const uri = `/api/document/${documentId}`;
    const apiStruct = {
      typeTag: documentType === 'None' ? null : documentType,
      description: description.trim(),
    };
    const request = createStdRequest(uri, 'put', apiStruct);
    const actionOnGoodResponse = (json) => {
      console.log(json);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStructForDocument(doc) {
    const inquiryId = this.state.inquiryId;
    const viewingFirmId = DA.getFirmId();
    return {
      ...doc,
      inquiryId,
      viewingFirmId,
    };
  }

  validateStateForUpdate() {
    const { email, phone } = this.state;
    if (email) {
      const validation = validateEmail(email, "The supplied email address doesn't look valid");
      if (!validation.valid) {
        return validation;
      }
    }
    if (phone) {
      return validatePhoneNumber(phone, 'The supplied phone number');
    }
    return { valid: true };
  }

  buildApiStructForUpdate() {
    const dateInWireFormat = this.state.date.toISO();
    const { chosenWordOfMouthOption, chosenSocialMediaOption, chosenMediaOption, referralOptionalSpecies } = this.state;
    return {
      ...this.state,
      date: dateInWireFormat,
      referralWordOfMouthType: chosenWordOfMouthOption,
      referralSocialMediaType: chosenSocialMediaOption,
      referralMediaType: chosenMediaOption,
      referralSourceSpecies: referralOptionalSpecies,
    };
  }

  offerToConvertInquiry() {
    this.setState({ shouldShowConversionModal: true });
  }

  maybeSaveThenConvertInquiry(conversionType) {
    if (!this.stateIsOriginal()) {
      const validation = this.validateStateForUpdate();
      if (!validation.valid) {
        showAlert(this, validation.errorMessage);
        return;
      }
      const inquiryId = this.state.inquiryId;
      const uri = `/api/inquiry/${inquiryId}`;
      const apiStruct = this.buildApiStructForUpdate();
      const request = createStdRequest(uri, 'PUT', apiStruct);
      const actionOnGoodResponse = (json) => {
        this.saveInquiryNotes();
        ReduxLoader.updateInquiry(apiStruct);
        const inquiryWithRealDate = DA.resolveInquiryDates(apiStruct);
        this.setStateFromInquiry(inquiryWithRealDate);
        this.convertInquiry(conversionType);
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
      this.convertInquiry(conversionType);
    }
  }

  convertInquiry(conversionType) {
    const primingInfo = {
      date: DateTime.local().toISO(),
      inquiryId: this.inquiryId,
      callRailCallId: this.callRailCallId,
      disposition: conversionType,
    };
    this.props.setInitialCallFormPrimingInfo(primingInfo);
    this.setState({ redirect: 'newProspectPage' });
  }

  confirmAndDelete() {
    const q = 'Are you sure you want to delete this Lead?';
    this.setState({
      shouldShowDeletionConfirmationModal: true,
      deletionConfirmationQuery: q,
      deleteFunc: () => this.deleteInquiry(),
    });
  }

  deleteInquiry() {
    const inquiryId = this.state.inquiryId;
    const uri = `/api/inquiry/${inquiryId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      this.props.removeInquiry(inquiryId);
      this.sendReduxUpdateToDeleteInquiryToWebSocket(inquiryId);
      this.props.history.goBack();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  sendReduxUpdateToDeleteInquiryToWebSocket(inquiryId) {
    const objToSend = {
      kind: 'inquiry',
      operation: 'delete',
      inquiryId: inquiryId,
    };
    SocketAccess.sendReduxUpdateToSocket(objToSend);
  }

  phoneNumberIsBad() {
    return !!this.state.phone && !validatePhoneNumber(this.state.phone).valid;
  }

  phoneNumberExistsAndIsBad() {
    if (!this.state.phone) {
      return false;
    }
    return this.phoneNumberIsBad();
  }

  addInquiryNote() {
    const notesList = this.state.notes;
    const blankNote = {
      noteId: 0,
      content: '',
      date: DateTime.local(),
    };
    const augmentedNotesList = [...notesList, blankNote];
    this.setState({ notes: augmentedNotesList });
  }

  removeInquiryNote(note) {
    if (note.noteId > 0) {
      const extendedDeletedNoteIds = [...this.state.deletedNoteIds, note.noteId];
      this.setState({ deletedNoteIds: extendedDeletedNoteIds });
    }
    const prunedNotes = this.state.notes.filter((el) => el !== note);
    this.setState({ notes: prunedNotes });
    this.notesHaveChanged = true;
  }

  backToInquiryTable() {
    if (!this.stateIsOriginal()) {
      const q = 'Are you sure you want to discard changes and go back to All Leads?';
      this.setState({
        shouldShowConfirmationInquiryModal: true,
        confirmationInquiryQuery: q,
        confirmInquiryFunc: () => this.props.history.goBack(),
      });
    } else {
      this.props.history.goBack();
    }
  }

  handleNoteChange(i, newValue) {
    const copiedList = this.state.notes.slice(0);
    copiedList[i].content = newValue;
    this.setState({ notes: copiedList });
    this.notesHaveChanged = true;
  }

  setReferralCategory(newCategory) {
    this.setState({ referralSourceCategory: newCategory, referralOptionalSpecies: '' });
  }

  setReferralOrgType(referralOrgType) {
    this.handleReferralOrgTypeChange(referralOrgType);
  }

  selectCompany(referralOrgType, referralOrgId) {
    this.setState({ referralOrgType, referralOrgId });
  }

  selectContact(referralOrgType, referralOrgId, personId) {
    this.setState({ referralOrgType, referralOrgId, referralContactId: personId });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;

    const users = DA.getAllFirmUsers();
    const namedOptions = users.map((u) => u.name);
    const realUserPersonIds = users.map((u) => u.personId);
    const userNameOptions = [...namedOptions, 'Unassigned'];
    const userPersonIdOptions = [...realUserPersonIds, 0];

    const dateString = dateStringForDateTime(this.state.date);
    const disableCancelButton = this.stateIsOriginal();
    const disableSaveButton = disableCancelButton || this.phoneNumberIsBad();
    const disableConvertButton = this.phoneNumberExistsAndIsBad();
    const extras = this.state.extras || [];
    const notes = this.state.notes || [];
    const campaigns = DA.getAllCampaigns();
    const sortedCampaigns = campaigns.sort((f1, f2) => {
      const f1Name = f1.name;
      const f2Name = f2.name;
      if (f1Name < f2Name) {
        return -1;
      } else if (f2Name < f1Name) {
        return 1;
      } else {
        return 0;
      }
    });

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <InquiryConversionModal
          show={this.state.shouldShowConversionModal}
          convertFunc={(conversionType) => this.maybeSaveThenConvertInquiry(conversionType)}
          closeFunc={() => this.setState({ shouldShowConversionModal: false })}
        />

        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.state.deleteFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.state.deletionConfirmationQuery}</div>
        </ConfirmModal>

        <ConfirmModal
          show={this.state.shouldShowConfirmationInquiryModal}
          title="Confirm Inquiry"
          confirmButtonLabel="Yes"
          rejectButtonLabel="No"
          onConfirm={this.state.confirmInquiryFunc}
          onReject={() => this.setState({ shouldShowConfirmationInquiryModal: false })}
        >
          <div>{this.state.confirmationInquiryQuery}</div>
        </ConfirmModal>

        <div className={classes.root}>
          <div className={classes.pageTitle}>Lead</div>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <div className={classes.valueDiv}>
                <CrmTextField label="Received" fullWidth disabled value={dateString} />
              </div>
            </Grid>
            <Grid item xs={12} sm={5}>
              <div className={classes.valueDiv}>
                <CrmTextField label="From" fullWidth disabled value={this.state.name} />
              </div>
            </Grid>
            <Grid item xs={12} sm={3}>
              <div className={classes.valueDiv}>
                <CrmTextField label="Source Type" fullWidth disabled value={this.state.source} />
              </div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <div className={classes.valueDiv}>
                <CrmTextField
                  label="Email"
                  fullWidth
                  value={this.state.email}
                  onChange={(event) => this.setState({ email: event.target.value.trim() })}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={5}>
              {true ? (
                <div className={classes.valueDiv}>
                  <CrmTextField
                    label="Phone"
                    fullWidth
                    value={this.state.phone}
                    onChange={(event) => this.handleNumberChange(event)}
                  />
                </div>
              ) : (
                <div className={classes.valueDiv}>
                  Phone:&nbsp;&nbsp;
                  <Input
                    className={classes.nativeInput}
                    value={this.state.phone}
                    onChange={(event) => this.handleNumberChange(event)}
                  />
                </div>
              )}
            </Grid>

            <Grid className={classes.selectionHierarchy} item xs={12} md={12}>
              <ReferralSelectionHierarchy
                searchableContactData={this.searchableContactData}
                chosenReferralCategory={this.state.referralSourceCategory}
                companyType={this.state.referralOrgType}
                companyId={this.state.referralOrgId}
                contactId={this.state.referralContactId}
                chosenWordOfMouthOption={this.state.chosenWordOfMouthOption}
                chosenSocialMediaOption={this.state.chosenSocialMediaOption}
                chosenMediaOption={this.state.chosenMediaOption}
                referralOptionalSpecies={this.state.referralOptionalSpecies}
                onReferralCategoryChange={(referralCategory) => this.setReferralCategory(referralCategory)}
                onCompanyTypeChange={(referralOrgType) => this.setReferralOrgType(referralOrgType)}
                onCompanyChange={(referralOrgType, referralOrgId) => this.selectCompany(referralOrgType, referralOrgId)}
                onContactChange={(referralOrgType, referralOrgId, personId) =>
                  this.selectContact(referralOrgType, referralOrgId, personId)
                }
                onWordOfMouthOptionChange={(newChoice) => this.setState({ chosenWordOfMouthOption: newChoice })}
                onSocialMediaOptionChange={(newChoice) => this.setState({ chosenSocialMediaOption: newChoice })}
                onMediaOptionChange={(newChoice) => this.setState({ chosenMediaOption: newChoice })}
                onOptionalSpeciesChange={(newValue) => this.setState({ referralOptionalSpecies: newValue })}
                onCreateNewCompany={(referralOrgType) => this.forceShowingOfNewReferrerCompanyModal(referralOrgType)}
                onCreateNewContact={(referralOrgType, firmId) =>
                  this.forceShowingOfNewContactModal(referralOrgType, firmId)
                }
              />
            </Grid>

            <Grid style={{ margin: '0 16px' }} container spacing={2}>
              <Grid item xs={12} md={4}>
                <KSelect
                  label="Owner"
                  fullWidth={true}
                  disabledOption="Select Owner (optional)"
                  liveOptions={userNameOptions}
                  liveValues={userPersonIdOptions}
                  value={this.state.ownerPersonId}
                  onChange={(event) => this.setState({ ownerPersonId: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KSelect
                  label="Temperature"
                  fullWidth={true}
                  disabledOption="Select Temperature"
                  liveOptions={temperatureOptions}
                  value={this.state.temperature}
                  onChange={(event) => this.setState({ temperature: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KSelect
                  label="Campaign"
                  fullWidth={true}
                  disabledOption='Select Campaign (or "None")'
                  liveOptions={['None'].concat(sortedCampaigns.map((c) => c.name))}
                  liveValues={[0].concat(sortedCampaigns.map((c) => c.campaignId))}
                  value={this.state.campaignId}
                  onChange={(event) => this.setState({ campaignId: event.target.value })}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} style={{ marginRight: '16px' }}>
              <div className={classes.valueDiv}>
                <CrmTextField
                  label="Message"
                  fullWidth
                  multiline
                  value={this.state.message}
                  onChange={(event) => this.setState({ message: event.target.value })}
                />
              </div>
            </Grid>
            <Grid item md={6}>
              <DocsAssocGroup
                docsSubjectProvider={this}
                allRelevantDocs={this.state.docs}
                clientId={0}
                inquiryId={this.state.inquiryId}
                parent={this}
              />
            </Grid>
            {extras.map((xtra, i) => (
              <Grid item md={11} key={i}>
                <div className={classes.valueDiv}>
                  <CrmTextField label={xtra.key} fullWidth multiline disabled value={xtra.value} />
                </div>
              </Grid>
            ))}
            {notes.map((note, i) => (
              <Grid item md={12} key={i}>
                <div className={classes.valueDiv}>
                  <CrmTextField
                    label="Note"
                    className={classes.divMessage}
                    fullWidth
                    multiline
                    value={note.content}
                    onChange={(event) => this.handleNoteChange(i, event.target.value)}
                  />
                  <Tooltip title="Delete" className={classes.delIcon} placement="top">
                    <DeleteIcon onClick={(event) => this.removeInquiryNote(note)} />
                  </Tooltip>
                </div>
              </Grid>
            ))}
          </Grid>
          <Grid item md={12}>
            <Button className={classes.newNoteButton} onClick={() => this.addInquiryNote()}>
              <PlusIcon /> Add Note
            </Button>
          </Grid>
          <Grid item md={11} className={classes.buttonsDiv}>
            <TooltippedButton
              classes={classes}
              tip="Back to All Leads"
              text="Cancel"
              action={() => this.backToInquiryTable()}
            />
            <TooltippedButton
              classes={classes}
              tip="Convert Lead into Prospect or Contact"
              text="Convert"
              disabled={disableConvertButton}
              filled
              action={() => this.offerToConvertInquiry()}
            />
            <TooltippedButton
              classes={classes}
              tip="Store changes to Database"
              text="Save"
              disabled={disableSaveButton}
              filled
              action={() => this.saveChanges()}
            />
          </Grid>
        </div>

        {this.shouldShowProprietorshipModal() && (
          <IcfConsultancyModal
            proprietorshipType={this.state.referralOrgType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewConsultancySuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelProprietorshipModal()}
          />
        )}

        {this.shouldShowReferrerFirmModal() && (
          <IcfReferrerFirmModal
            firmType={this.state.referralOrgType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewReferrerFirmSuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelReferrerFirmModal()}
          />
        )}

        {this.shouldShowPractitionerModal() && (
          <IcfPractitionerModal
            firmType={this.state.referralOrgType}
            onSuccess={(visibleFirmType, visibleFirmId, contactId) =>
              this.handleNewPractitionerSuccess(visibleFirmType, visibleFirmId, contactId)
            }
            onCancel={() => this.cancelPractitionerModal()}
          />
        )}

        {this.shouldShowNewOrgContactModal() && (
          <IcfOrgContactModal
            firmId={this.state.referralOrgId}
            onSuccess={(contactId) => this.handleNewOrgContactSuccess(contactId)}
            onCancel={() => this.cancelContactModal()}
          />
        )}

        {this.shouldShowNewUnaffilContactModal() && (
          <IcfUnaffiliatedContactModal
            onSuccess={(contactId) => this.handleNewUnaffilContactSuccess(contactId)}
            onCancel={() => this.cancelContactModal()}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  inquiryId: state.intendedInfo.intendedInquiryId,
  knownFirms: state.firmInfo.knownFirms,
});

const actionProducers = {
  removeInquiry,
  setInitialCallFormPrimingInfo,
  addOrUpdateNotesOnInquiries,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(InquiryPage));
